import { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export const PortContext = createContext();

export const usePortContext = () => useContext(PortContext);

// eslint-disable-next-line react/prop-types
export const PortProvider = ({ children }) => {
  const [port, setPort] = useState(null);
  const [mode, setMode] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const [updateUi, setUpdateUi] = useState(false);

  const [data, setData] = useState('');
  const [analysisId, setAnalysisId] = useState('');
  const [analysisReport, setAnalysisReport] = useState({});
  const [hasAnalysisFailed, setHasAnalysisFailed] = useState(false);
  const [historyData, setHistoryData] = useState('');
  const [keypointsData, setKeypointsData] = useState([]);
  const [boundingBoxData, setBoundingBoxData] = useState({});
  const [image, setImage] = useState(null);
  const [depthMap, setDepthMap] = useState(null);
  const [isDebug, setIsDebug] = useState(false);
  const [lang, setLang] = useState('en');

  const navigate = useNavigate();

  const animationProcessingEffect = () => {
    if (!image) {
      return;
    }
    if (!Object.values(keypointsData).length) {
      return;
    }
    if (!Object.values(boundingBoxData).length) {
      return;
    }

    const worker = new Worker(new URL('../service/worker.js', import.meta.url));
    worker.postMessage({ image, keypoints: keypointsData, box: boundingBoxData });
    worker.onmessage = ({ data }) => {
      setDepthMap(data);
    };

    return () => {
      worker.terminate();
    };
  };

  const handlePhotoUploadEvent = (data) => {
    setAnalysisId(data.analysisId);
  };

  const handleDataSubmittedEvent = (data) => {
    console.log('data submitted event received');
    console.log(data);
  };

  const handleAnalysisCompletedEvent = (data) => {
    data.scores = getScoreWithConcerns(data);
    setAnalysisReport(data);
    navigate('/skinReport');
  };

  const handleAnalysisFailedEvent = () => {
    setHasAnalysisFailed(true);
  };

  const handleHistoryDataReceived = (data) => {
    data?.analyses.forEach((analysis) => (analysis.scores = getScoreWithConcerns(analysis)));
    setHistoryData({
      analyses: data?.analyses.sort((a, b) => (new Date(a.createdAt) < new Date(b.createdAt) ? 1 : -1)),
      insights: data?.insights,
    });
    navigate('/mySkinDiary');
  };

  const handleChangeLang = (data) => {
    const lang = data.lang;
    if (['id', 'en'].includes(lang)) {
      setLang(lang);
    }
  };

  const emitLoginEvent = (analysisId) => {
    if (!port) {
      return;
    }
    port.postMessage({ eventName: 'login', data: { analysisId } });
  };

  const emitDeleteAllEvent = () => {
    if (!port) {
      return;
    }
    port.postMessage({ eventName: 'deleteAll' });
    setHistoryData((historyData) => ({ ...historyData, analyses: [] }));
  };

  const emitDeleteAnalysisEvent = (analysisId) => {
    setHistoryData((historyData) => {
      const newAnalyses = historyData.analyses.filter((analysis) => analysis.analysisId !== analysisId);
      return { ...historyData, analyses: newAnalyses };
    });
    if (!port) {
      return;
    }
    port.postMessage({ eventName: 'deleteAnalysis', data: { analysisId } });
  };

  const emitAddToCartEvent = (productId) => {
    if (!port) {
      return;
    }
    port.postMessage({ eventName: 'addToCart', data: { productId } });
  };

  const emitUploadPhotoEvent = (image, points) => {
    if (!port) {
      return;
    }

    setHasAnalysisFailed(false);

    port.postMessage({ eventName: 'uploadPhoto', data: { image, points } });
  };

  const emitSubmitDataEvent = (analysisId, data) => {
    if (!port) {
      return;
    }
    data = { ...data, analysisId };
    port.postMessage({ eventName: 'submitData', data });
  };

  const emitGetHistoryEvent = () => {
    if (!port) {
      return;
    }
    port.postMessage({ eventName: 'getHistory' });
  };

  const emitSubmitUserDataEvent = (data) => {
    if (!port) {
      return;
    }
    port.postMessage({ eventName: 'submitUserData', data });
  };

  const events = {
    emitLoginEvent,
    emitDeleteAllEvent,
    emitDeleteAnalysisEvent,
    emitAddToCartEvent,
    emitUploadPhotoEvent,
    emitSubmitDataEvent,
    emitGetHistoryEvent,
    emitSubmitUserDataEvent,
  };

  useEffect(animationProcessingEffect, [image, keypointsData, boundingBoxData]);

  /**
   * @param {MessageEvent} param0
   */
  const portMessageHandler = async ({ data: event }) => {
    switch (event.eventName) {
      case 'photoUploaded':
        handlePhotoUploadEvent(event.data);
        break;
      case 'dataSubmitted':
        handleDataSubmittedEvent(event.data);
        break;
      case 'analysisCompleted':
        handleAnalysisCompletedEvent(event.data);
        break;
      case 'analysisFailed':
        handleAnalysisFailedEvent(event.data);
        break;
      case 'reportHistory':
        handleHistoryDataReceived(event.data);
        break;
      case 'switchLang':
        handleChangeLang(event.data);
        break;
    }
    setData(event);
  };

  /**
   * @param {MessageEvent} param0
   */
  const parentMessageHandler = ({ data: event, ports: [port] }) => {
    if (!event || event?.type !== 'init') {
      return;
    }

    if (!port) {
      throw new Error('port not found');
    }

    port.onmessage = portMessageHandler;
    setPort(port);
    setLang(event.lang || 'en');
    setIsDebug(!!event.isDebug);
    switch (event?.mode) {
      case 'newAnalysis':
        setMode('newAnalysis');
        setIsLoggedIn(event.data?.isLoggedIn);
        navigate('/landing');
        break;

      case 'analysisReport':
        event.data.scores = getScoreWithConcerns(event.data);
        setMode('analysisReport');
        setAnalysisReport(event.data);
        setIsLoggedIn(true);
        navigate('/skinReport');
        break;

      case 'historyView':
        setMode('historyView');
        event.data?.analyses.forEach((analysis) => {
          analysis.scores = getScoreWithConcerns(analysis);
        });
        setHistoryData({
          analyses: event.data?.analyses.sort((a, b) => (new Date(a.createdAt) < new Date(b.createdAt) ? 1 : -1)),
          insights: event.data?.insights,
        });
        setIsLoggedIn(true);
        navigate('/mySkinDiary');
        break;

      case 'offlineView':
        setMode('offlineView');
        setIsLoggedIn(true);
        navigate('/landing');
        break;
      default:
        throw new Error('unknown mode');
    }
  };

  useEffect(() => {
    window.addEventListener('message', parentMessageHandler);

    if (port) {
      port.onmessage = portMessageHandler;
    }

    return () => {
      window.removeEventListener('message', parentMessageHandler);
      if (port) {
        port.onmessage = null;
      }
    };
  }, [port]);

  return (
    <PortContext.Provider
      value={{
        events,
        port,
        mode,
        data,
        analysisId,
        updateUi,
        setUpdateUi,
        isLoggedIn,
        analysisReport,
        historyData,
        setBoundingBoxData,
        boundingBoxData,
        setKeypointsData,
        keypointsData,
        image,
        setImage,
        depthMap,
        lang,
        setHistoryData,
        setAnalysisReport,
        hasAnalysisFailed,
        isDebug,
      }}>
      {children}
    </PortContext.Provider>
  );
};

const scoreIssueMap = {
  acne: 'acnePimples',
  poreSize: 'enlargedPores',
  texture: 'roughTexture',
  brightness: 'dullSkin',
  darkSpots: 'darkSpotsPigmentation',
};

const getScoreWithConcerns = (report) => {
  const { issues, scores } = report;

  const newScoreEntries = Object.entries(scores).map(([scoreKey, scoreValue]) => {
    if (scoreKey === 'skinHealth') {
      return [scoreKey, scoreValue];
    }

    let val = scoreValue;

    val -= issues.length > 3 ? 3 : issues.length;

    val -= issues.includes(scoreIssueMap[scoreKey]) ? 1 : 0;

    return [scoreKey, val < 4 ? 4 : Math.round(val)];
  });

  const averageScore =
    newScoreEntries.reduce((acc, [key, value]) => acc + (key === 'skinHealth' ? 0 : value), 0) / (newScoreEntries.length - 1);

  const newScores = Object.fromEntries(newScoreEntries);
  newScores.skinHealth = Math.round(averageScore);

  return newScores;
};
